import React from "react";
import {
  FileText,
  Search,
  CheckCircle,
  RotateCcw,
  XCircle,
  Plus,
  AlertCircle,
} from "lucide-react";
import {
  NewTrackingModel,
  TrackingItem,
  FinalProduct,
  AddedProduct,
} from "../../../interface";

interface NewTrackingDisplayProps {
  trackingData: NewTrackingModel;
}

export const NewTrackingDisplay: React.FC<NewTrackingDisplayProps> = ({
  trackingData,
}) => {
  return (
    <div className="tracking-history">
      <h6>Histórico de Alterações:</h6>

      {/* Exames Normalizados */}
      {trackingData.exams_normalized.length > 0 && (
        <div className="track-section">
          <div className="section-title">
            <FileText size={16} />
            Exames Identificados pela IA:
          </div>
          <div className="exams-list">
            {trackingData.exams_normalized.map(
              (exam: string, index: number) => (
                <span key={index} className="exam-tag">
                  {exam}
                </span>
              )
            )}
          </div>
        </div>
      )}

      {/* Produtos Processados */}
      {trackingData.items.length > 0 && (
        <div className="track-section">
          <div className="section-title">
            <Search size={16} />
            Produtos Processados:
          </div>
          {trackingData.items.map((item: TrackingItem, index: number) => (
            <div key={index} className="track-item">
              <div className="track-original">
                <strong>Original:</strong> {item.original_product.original_name}
                <span className="match-info">
                  (Encontrado: {item.original_product.found_name})
                </span>
                <span className="confidence-info">
                  Confiança:{" "}
                  {(item.original_product.details.confidence * 100).toFixed(1)}%
                </span>
              </div>
              <div className="track-status">
                <strong>Status:</strong>
                <span className={`status-${item.final_status}`}>
                  {item.final_status === "kept" && (
                    <>
                      <CheckCircle size={14} />
                      Mantido
                    </>
                  )}
                  {item.final_status === "replaced" && (
                    <>
                      <RotateCcw size={14} />
                      Substituído
                    </>
                  )}
                  {item.final_status === "removed" && (
                    <>
                      <XCircle size={14} />
                      Removido
                    </>
                  )}
                </span>
              </div>
              {item.final_status === "replaced" && item.final_products && (
                <div className="track-replacements">
                  <strong>Substituído por:</strong>
                  <ul>
                    {item.final_products.map(
                      (product: FinalProduct, prodIndex: number) => (
                        <li key={prodIndex}>
                          {product.name} (ID: {product.id})
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Produtos Adicionados Manualmente */}
      {trackingData.added_products.length > 0 && (
        <div className="track-section">
          <div className="section-title">
            <Plus size={16} />
            Produtos Adicionados Manualmente:
          </div>
          <ul>
            {trackingData.added_products.map(
              (product: AddedProduct, index: number) => (
                <li key={index}>
                  {product.name} (ID: {product.id})
                </li>
              )
            )}
          </ul>
        </div>
      )}

      {/* Produtos Não Encontrados */}
      {trackingData.not_founded.length > 0 && (
        <div className="track-section">
          <div className="section-title">
            <AlertCircle size={16} />
            Produtos Não Encontrados:
          </div>
          <ul>
            {trackingData.not_founded.map(
              (productName: string, index: number) => (
                <li key={index} className="not-found-item">
                  {productName}
                </li>
              )
            )}
          </ul>
        </div>
      )}

      <style>{`
        .tracking-history {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 16px;
          margin: 16px 0;
          max-height: 400px;
          overflow-y: auto;
        }

        .track-section {
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #dee2e6;
        }

        .track-section:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .section-title {
          font-size: 14px;
          font-weight: 600;
          color: #495057;
          margin-bottom: 12px;
          background: #fff;
          padding: 8px 12px;
          border-radius: 6px;
          border-left: 4px solid #007bff;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .exams-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .exam-tag {
          background: #e3f2fd;
          color: #1565c0;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          border: 1px solid #bbdefb;
        }

        .track-item {
          background: #fff;
          border: 1px solid #e9ecef;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 12px;
        }

        .track-original {
          margin-bottom: 8px;
          font-size: 14px;
        }

        .track-original strong {
          color: #495057;
        }

        .match-info {
          color: #6c757d;
          font-size: 12px;
          margin-left: 8px;
          font-style: italic;
        }

        .confidence-info {
          color: #28a745;
          font-size: 11px;
          margin-left: 8px;
          font-weight: 500;
        }

        .track-status {
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          flex-wrap: nowrap;
        }

        .track-status strong {
          color: #495057;
          margin-right: 8px;
        }

        .status-kept {
          color: #28a745;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .status-replaced {
          color: #ffc107;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .status-removed {
          color: #dc3545;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .track-replacements {
          background: #f8f9fa;
          padding: 8px;
          border-radius: 4px;
          margin-top: 8px;
        }

        .track-replacements strong {
          color: #495057;
          font-size: 13px;
        }

        .track-replacements ul {
          margin: 8px 0 0 0;
          padding-left: 20px;
        }

        .track-replacements li {
          font-size: 13px;
          color: #6c757d;
        }

        .not-found-item {
          color: #dc3545;
          font-size: 13px;
          font-style: italic;
        }

        .tracking-history ul {
          margin: 8px 0;
          padding-left: 20px;
        }

        .tracking-history li {
          font-size: 13px;
          color: #6c757d;
          margin-bottom: 4px;
        }
      `}</style>
    </div>
  );
};
